// =============================================
// src/hooks/useWorkflow.js
// =============================================

import { useEffect, useState } from "react";
import { parseWorkflow } from "../utils/parseWorkflow";
import { generateWorkflowPrompt } from "../utils/generateWorkflowPrompt";

export function useWorkflow() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [workflow, setWorkflow] = useState(null);
  const [parsedSteps, setParsedSteps] = useState([]);
  const [activeStepIndex, setActiveStepIndex] = useState(-1);
  const [executing, setExecuting] = useState(false);
  const [dataSource, setDataSource] = useState("");

  const isWorkflowComplete =
    parsedSteps.length > 0 && !executing && activeStepIndex >= parsedSteps.length - 1;

  async function runWorkflow() {
    if (!query.trim()) {
      alert("Please enter a research query");
      return;
    }

    setLoading(true);
    setParsedSteps([]);
    setActiveStepIndex(-1);
    setWorkflow(null);

    try {
      const response = await fetch("/api/workflow", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "gpt-4o",
          messages: [
            {
              role: "system",
              content:
                "You are an expert AI agent for AlzTxPILOT, specialized in drug target prioritization in Alzheimer's disease research. Generate detailed workflows with specific tool names, descriptions, and realistic mock outputs. Always return valid JSON format.",
            },
            {
              role: "user",
              content: generateWorkflowPrompt(query),
            },
          ],
          temperature: 0.7,
          max_tokens: 2000,
        }),
      });

      if (!response.ok) throw new Error(`API request failed (${response.status})`);

      const data = await response.json();
      const workflowText = data.choices?.[0]?.message?.content ?? "";
      if (!workflowText) throw new Error("No workflow generated");

      setWorkflow(workflowText);
      const result = parseWorkflow(workflowText);
      setParsedSteps(result.steps);
      setDataSource(result.source);
      setExecuting(true);
      setActiveStepIndex(0);
    } catch (error) {
      console.error("Error generating workflow:", error);
      const mockResult = parseWorkflow("");
      setParsedSteps(mockResult.steps);
      setDataSource(mockResult.source);
      setExecuting(true);
      setActiveStepIndex(0);
    } finally {
      setLoading(false);
    }
  }

  // Auto-advance logic
  useEffect(() => {
    if (executing && activeStepIndex >= 0 && activeStepIndex < parsedSteps.length - 1) {
      const timer = setTimeout(() => setActiveStepIndex((prev) => prev + 1), 5000);
      return () => clearTimeout(timer);
    } else if (activeStepIndex >= parsedSteps.length - 1 && executing) {
      const timer = setTimeout(() => setExecuting(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [activeStepIndex, executing, parsedSteps.length]);

  return {
    query,
    setQuery,
    loading,
    workflow,
    parsedSteps,
    activeStepIndex,
    executing,
    dataSource,
    isWorkflowComplete,
    runWorkflow,
    reset: () => {
      setQuery("");
      setParsedSteps([]);
      setActiveStepIndex(-1);
      setWorkflow(null);
      setExecuting(false);
      setDataSource("");
    },
  };
}